import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { Constants } from '@/assets'
import { UserServiceHelper } from '@/modules/User'
import NextImage from 'next/image'

import Box from '@mui/material/Box'
import VariantImages from '@/assets/VariantImages'
import { Coordinate } from '@/data'
import { EmtButton, EmtIcon, EmtTypography } from 'emt-components-v2'

type Props = React.PropsWithChildren<{
  address: Coordinate
  editButton?: boolean
  title?: string
  editButtonOnClick?: () => void
  color?:
    | 'primary'
    | 'secondary'
    | 'default'
    | 'inherit'
    | undefined
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'purple'
}>

const ProfileMap: React.FC<Props> = ({ address, editButton = false, editButtonOnClick, color = 'primary', title }) => {
  const { t } = useTranslation('profile')
  const mapRef = useRef<HTMLDivElement | null>(null)
  const [zoom, setZoom] = useState(14)
  const [city, setCity] = useState<string | undefined>(undefined)
  const [zipCode, setZipCode] = useState<string | undefined>(undefined)

  const getLocation = async (latitude: number, longitude: number) => {
    try {
      const address = await UserServiceHelper.searchAddressAsync2({
        geolocation: {
          latitude: latitude.toString(),
          longitude: longitude.toString(),
        },
        strict: true,
      })

      setCity(address[0]?.city)
      setZipCode(address[0]?.zipCode)

      return [address]
    } catch (error) {
      console.error('Error fetching location:', error)
      setCity(undefined)
      setZipCode(undefined)
      return []
    }
  }

  const lastCoordsRef = useRef<string | null>(null)

  useEffect(() => {
    if (!address?.latitude || !address?.longitude) return

    const key = `${address.latitude}-${address.longitude}`

    if (lastCoordsRef.current === key) return
    lastCoordsRef.current = key

    getLocation(address.latitude, address.longitude)
  }, [address?.latitude, address?.longitude])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '14px' }}>
      <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        {/* Show city if available */}
        {title ? (
          <EmtTypography variant="h5" fontWeight={700} sx={{ lineHeight: '1' }}>
            {title}
          </EmtTypography>
        ) : city && zipCode ? (
          <EmtTypography variant="h5" fontWeight={700} sx={{ lineHeight: '1' }}>
            {city} - {zipCode}
          </EmtTypography>
        ) : (
          <EmtTypography variant="h5" fontWeight={700} sx={{ lineHeight: '1' }}>
            {t('address.title')}
          </EmtTypography>
        )}
        {!title && (
          <EmtTypography variant="body2" sx={{ lineHeight: '1' }}>
            {t('address.locationApproximate')} :
          </EmtTypography>
        )}
      </Box>

      <Box
        ref={mapRef}
        sx={{
          height: 260,
          borderRadius: '18px',
          overflow: 'hidden',
          position: 'relative',
          '& .place-card': {
            display: 'none',
          },
        }}
      >
        <div style={{ height: '100%', width: '100%' }}>
          {color === 'purple' ? (
            <NextImage
              style={{ zIndex: 9, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -90%)' }}
              src={VariantImages.PinSitter}
              width={46}
              alt="Map pin"
            />
          ) : (
            <img
              src={VariantImages.MapPinUrl}
              alt="Map pin"
              style={{ zIndex: 9, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            />
          )}
          <iframe
            id="googlemapiframe"
            key={zoom}
            width="100%"
            height="100%"
            style={{
              border: 0,
              pointerEvents: 'none',
              position: 'relative',
              zIndex: 1,

              width: '130%',
              left: '-15%',
              height: '130%',
              top: '-15%',
            }}
            src={`https://www.google.com/maps/embed/v1/place?key=${Constants.gMapKey}&q=${address.latitude}+${address.longitude}+wsq+q&center=${address.latitude}+${address.longitude}&zoom=${zoom}`}
          />
        </div>
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <EmtButton
            // variant="outlined"
            color={color}
            onClick={() => setZoom((prev) => Math.min(prev + 1, 17))}
            sx={{ minWidth: '50px', width: '50px', height: '50px', '& .emt-icons': { fontSize: '18px!important' } }}
          >
            <EmtIcon className="emt-add" />
          </EmtButton>
          <EmtButton
            variant="whiteOutlined"
            color={color}
            onClick={() => setZoom((prev) => Math.max(prev - 1, 8))}
            sx={{ minWidth: '50px', width: '50px', height: '50px', '& .emt-icons': { fontSize: '18px!important' } }}
          >
            <EmtIcon className="emt-minus" />
          </EmtButton>
        </Box>
        {editButton ? (
          <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 10 }}>
            <EmtButton color={color} onClick={editButtonOnClick} startIcon={<EmtIcon className="emt-edit" />}>
              {t('address.edit.editAddressButton')}
            </EmtButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default ProfileMap
